import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  ArrowLeft, 
  Volume2, 
  VolumeX, 
  User, 
  Palette,
  Shield,
  HelpCircle,
  Save,
  RotateCcw,
  Play,
  CheckCircle,
  AlertTriangle,
  Trash2,
  ChevronDown,
  ChevronUp,
  LogOut,
  Settings
} from 'lucide-react'
import { authService, AuthUser } from '@/services/auth'
import { audioService } from '@/services/audio'
import { useTranslation } from 'react-i18next'
import { preferencesService } from '@/services/preferences'
import { Wand2 } from 'lucide-react'

export default function SettingsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [saveType, setSaveType] = useState<'success' | 'error' | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [pendingNav, setPendingNav] = useState<null | { type: 'back' } | { type: 'path', path: string }>(null)
  
  // Audio settings
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(1.0)
  const [testingSound, setTestingSound] = useState<string | null>(null)

  // Other settings
  const [theme, setTheme] = useState('dark')

  // Prompt translation settings
  const [promptTranslationEnabled, setPromptTranslationEnabled] = useState<boolean>(true)
  const [initialSettings, setInitialSettings] = useState<{ theme: string; promptEnabled: boolean } | null>(null)

  useEffect(() => {
    const loadUserAndSettings = async () => {
      try {
        const user = await authService.getCurrentUser()
        if (!user) {
          navigate('/login')
          return
        }
        setCurrentUser(user)

        // Load audio settings
        setIsMuted(audioService.getMuted())
        setVolume(audioService.getVolume())

        // Load other settings from localStorage
        const savedTheme = localStorage.getItem('theme')
        const pt = preferencesService.getPromptTranslation()
        
        if (savedTheme) {
          setTheme(savedTheme)
        }
        setPromptTranslationEnabled(pt.enabled)
        setInitialSettings({
          theme: savedTheme || 'dark',
          promptEnabled: pt.enabled,
        })
      } catch (error) {
        console.error('Error loading settings:', error)
      } finally {
        setLoading(false)
      }
    }

    loadUserAndSettings()
  }, [navigate])

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume)
    audioService.setVolume(newVolume)
    
    // If volume is set above 0, unmute automatically
    if (newVolume > 0 && isMuted) {
      setIsMuted(false)
      audioService.setMuted(false)
    }
  }
  const isDirty = initialSettings ? (
    theme !== initialSettings.theme ||
    promptTranslationEnabled !== initialSettings.promptEnabled
  ) : false

  const handleNavigateBack = () => {
    if (isDirty) {
      setPendingNav({ type: 'back' })
      setShowUnsavedModal(true)
      return
    }
    navigate(-1)
  }

  const handleNavigateTo = (path: string) => {
    if (isDirty) {
      setPendingNav({ type: 'path', path })
      setShowUnsavedModal(true)
      return
    }
    navigate(path)
  }

  const proceedNavigation = () => {
    if (!pendingNav) return
    if (pendingNav.type === 'back') navigate(-1)
    if (pendingNav.type === 'path') navigate(pendingNav.path)
    setPendingNav(null)
  }


  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [understood, setUnderstood] = useState(false)
  const [showMoreActions, setShowMoreActions] = useState(false)
  const [showDangerZone, setShowDangerZone] = useState(false)

  const handleDeleteAccount = async () => {
    if (!currentUser) return
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!currentUser) return
    try {
      setDeleting(true)
      await authService.deleteAccount()
      navigate('/')
    } catch (err) {
      console.error('Delete account failed:', err)
      alert(t('settings.delete.error') as string)
    } finally {
      setDeleting(false)
      setShowDeleteModal(false)
      setUnderstood(false)
    }
  }

  const handleMuteToggle = () => {
    const newMuted = !isMuted
    setIsMuted(newMuted)
    audioService.setMuted(newMuted)
  }

  const handleTestSound = async (soundKey: string) => {
    if (testingSound) return // Prevent multiple simultaneous tests
    
    setTestingSound(soundKey)
    try {
      await audioService.testSound(soundKey)
    } catch (error) {
      console.error('Error testing sound:', error)
    }
    
    // Reset after a short delay
    setTimeout(() => {
      setTestingSound(null)
    }, 1000)
  }

  const handleSaveSettings = async () => {
    setSaving(true)
    setSaveMessage(null)
    setSaveType(null)

    try {
      // Save to localStorage
      localStorage.setItem('theme', theme)
      preferencesService.setPromptTranslationEnabled(promptTranslationEnabled)
      // hedef dil ipucunu artık tutmuyoruz
      
      // Audio settings are already saved automatically by audioService
      setSaveMessage(t('settings.saveSuccess'))
      setSaveType('success')
      
      setTimeout(() => {
        setSaveMessage(null)
        setSaveType(null)
      }, 3000)
    } catch (error) {
      console.error('Error saving settings:', error)
      setSaveMessage(t('settings.saveError'))
      setSaveType('error')
    } finally {
      setSaving(false)
    }
  }

  const handleResetSettings = () => {
    // Reset to defaults
    setIsMuted(false)
    setVolume(1.0)
    setTheme('dark')
    
    // Apply audio changes
    audioService.setMuted(false)
    audioService.setVolume(1.0)
    
    setSaveMessage(t('settings.resetSuccess'))
    setSaveType('success')
    setTimeout(() => {
      setSaveMessage(null)
      setSaveType(null)
    }, 3000)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-white/20 border-t-violet-400 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-16 h-16 border-2 border-purple-400/30 rounded-full animate-pulse mx-auto"></div>
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-white">{t('common.loading')}</h3>
            <p className="text-white/70">...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative"
      >
        {/* Mobile: back (left), title (center), profile (right) */}
        <div className="sm:hidden px-4 mb-3">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <motion.button
                onClick={handleNavigateBack}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{t('common.back')}</span>
              </motion.button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-xl font-bold text-gradient">Settings</h1>
                <p className="text-white/60 text-xs">Configure your preferences</p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={() => handleNavigateTo('/profile')}
                className="btn-secondary flex items-center space-x-2"
              >
                <User className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Desktop layout unchanged */}
        <div className="hidden sm:flex items-center justify-between px-4">
          <motion.button
            onClick={handleNavigateBack}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-secondary flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('common.back')}</span>
          </motion.button>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gradient">Settings</h1>
            <p className="text-white/60 text-base">Configure your preferences</p>
          </div>
          <button
            onClick={() => handleNavigateTo('/profile')}
            className="btn-secondary flex items-center space-x-2"
          >
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">Profile</span>
          </button>
        </div>
      </motion.div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 safe-area"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            className="card-gradient max-w-md w-full border-red-400/50"
          >
            <div className="text-center mb-4">
              <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Trash2 className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{t('settings.delete.confirmTitle')}</h3>
              <p className="text-white/80 text-sm mb-1">{t('settings.delete.confirmBody1')}</p>
              <p className="text-white/60 text-sm">{t('settings.delete.confirmBody2')}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 mb-4 border border-white/10 flex items-center space-x-2">
              <input
                id="understood"
                type="checkbox"
                checked={understood}
                onChange={(e) => setUnderstood(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="understood" className="text-white/80 text-sm cursor-pointer">{t('settings.delete.understand')}</label>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => { setShowDeleteModal(false); setUnderstood(false) }}
                className="flex-1 py-3 px-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl border border-white/20 transition-all"
                disabled={deleting}
              >
                {t('settings.delete.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                disabled={!understood || deleting}
                className={`flex-1 py-3 px-4 rounded-xl text-white font-semibold transition-all shadow-lg ${
                  (!understood || deleting)
                    ? 'bg-red-500/20 cursor-not-allowed'
                    : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800'
                }`}
              >
                {deleting ? t('settings.delete.deleting') : t('settings.delete.confirm')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Save Message */}
      {saveMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mx-4 p-4 rounded-xl border flex items-center space-x-2 ${
            saveType === 'error' 
              ? 'bg-red-500/10 border-red-500/30 text-red-300'
              : 'bg-green-500/10 border-green-500/30 text-green-300'
          }`}
        >
          {saveType === 'error' ? (
            <AlertTriangle className="w-5 h-5" />
          ) : (
            <CheckCircle className="w-5 h-5" />
          )}
          <span>{saveMessage}</span>
        </motion.div>
      )}

      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 safe-area"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            className="card-gradient max-w-md w-full border-yellow-400/50"
          >
            <div className="text-center mb-4">
              <div className="w-14 h-14 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{t('settings.unsaved.title')}</h3>
              <p className="text-white/80 text-sm mb-1">{t('settings.unsaved.body')}</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowUnsavedModal(false)}
                className="flex-1 py-3 px-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl border border-white/20 transition-all"
              >
                {t('settings.unsaved.cancel')}
              </button>
              <button
                onClick={async () => { await handleSaveSettings(); setShowUnsavedModal(false); proceedNavigation() }}
                className="flex-1 py-3 px-4 rounded-xl text-white font-semibold transition-all shadow-lg bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
              >
                {t('settings.unsaved.save')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Audio Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-4 mx-4"
      >
        <h2 className="text-xl font-bold text-white flex items-center space-x-2">
          <Volume2 className="w-5 h-5 text-violet-400" />
          <span>{t('settings.audio')}</span>
        </h2>

        <div className="card-gradient space-y-6">
          {/* Mute Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h3 className="font-semibold text-white">{t('settings.mute')}</h3>
              <p className="text-white/60 text-sm">{t('settings.muteDesc')}</p>
            </div>
            <button
              onClick={handleMuteToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isMuted ? 'bg-gray-600' : 'bg-violet-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isMuted ? 'translate-x-1' : 'translate-x-6'
                }`}
              />
            </button>
          </div>

          {/* Volume Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-white">{t('settings.volume')}</h3>
              <span className="text-white/70 text-sm">{Math.round(volume * 100)}%</span>
            </div>
            <div className="flex items-center space-x-3">
              <VolumeX className="w-4 h-4 text-white/60" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                className="flex-1 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer slider"
                disabled={isMuted}
              />
              <Volume2 className="w-4 h-4 text-white/60" />
            </div>
          </div>

          {/* Sound Tests */}
          <div className="space-y-3">
            <h3 className="font-semibold text-white">{t('settings.soundTests')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => handleTestSound('timer-warning')}
                disabled={testingSound === 'timer-warning' || isMuted}
                className="btn-secondary flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {testingSound === 'timer-warning' ? (
                  <div className="w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                <span>{t('settings.timerWarning')}</span>
              </button>

              <button
                onClick={() => handleTestSound('prompt-lock')}
                disabled={testingSound === 'prompt-lock' || isMuted}
                className="btn-secondary flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {testingSound === 'prompt-lock' ? (
                  <div className="w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                <span>{t('settings.promptLock')}</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>


      {/* Prompt Translation Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-4 mx-4"
      >
        <h2 className="text-xl font-bold text-white flex items-center space-x-2">
          <Wand2 className="w-5 h-5 text-amber-400" />
          <span>{t('settings.promptTranslation')}</span>
        </h2>

        <div className="card-gradient space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1 pr-4 flex-1">
              <h3 className="font-semibold text-white">{t('settings.promptTranslationEnable')}</h3>
              <p className="text-white/60 text-sm">{t('settings.promptTranslationEnableDesc')}</p>
            </div>
            <button
              onClick={() => setPromptTranslationEnabled(!promptTranslationEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
                promptTranslationEnabled ? 'bg-amber-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  promptTranslationEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Appearance Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="space-y-4 mx-4"
      >
        <h2 className="text-xl font-bold text-white flex items-center space-x-2">
          <Palette className="w-5 h-5 text-pink-400" />
          <span>{t('settings.appearance')}</span>
        </h2>

        <div className="card-gradient space-y-4">
          <div className="space-y-3">
            <h3 className="font-semibold text-white">{t('settings.theme')}</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setTheme('dark')}
                className={`p-3 rounded-xl border-2 transition-colors ${
                  theme === 'dark' 
                    ? 'border-violet-400 bg-violet-500/20' 
                    : 'border-white/20 bg-white/5 hover:bg-white/10'
                }`}
              >
                <div className="text-center">
                  <div className="w-8 h-8 bg-gray-800 rounded-lg mx-auto mb-2"></div>
                  <span className="text-white text-sm">{t('settings.themeDark')}</span>
                </div>
              </button>

              <button
                onClick={() => setTheme('light')}
                className={`p-3 rounded-xl border-2 transition-colors opacity-50 cursor-not-allowed ${
                  theme === 'light' 
                    ? 'border-violet-400 bg-violet-500/20' 
                    : 'border-white/20 bg-white/5'
                }`}
                disabled
              >
                <div className="text-center">
                  <div className="w-8 h-8 bg-gray-200 rounded-lg mx-auto mb-2"></div>
                  <span className="text-white text-sm">{t('settings.themeLight')}</span>
                  <p className="text-white/40 text-xs">{t('settings.soon')}</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Enhanced Account Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="space-y-4 mx-4"
      >
        <h2 className="text-xl font-bold text-white flex items-center space-x-2">
          <Shield className="w-5 h-5 text-green-400" />
          <span>{t('settings.account')}</span>
        </h2>

        <div className="card-gradient space-y-6">
          {/* Basic Account Info */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-white/80">{t('settings.username')}</span>
              <span className="text-white">{currentUser?.username || 'Belirtilmemiş'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/80">{t('settings.email')}</span>
              <span className="text-white text-sm">{currentUser?.email}</span>
            </div>
          </div>

          {/* More Actions Dropdown */}
          <div className="border-t border-white/10 pt-4">
            <button
              onClick={() => setShowMoreActions(!showMoreActions)}
              className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Settings className="w-5 h-5 text-blue-400" />
                <span className="text-white font-medium">{t('settings.moreActions')}</span>
              </div>
              {showMoreActions ? (
                <ChevronUp className="w-5 h-5 text-white/60" />
              ) : (
                <ChevronDown className="w-5 h-5 text-white/60" />
              )}
            </button>

            {/* More Actions Content */}
            {showMoreActions && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 space-y-2 overflow-hidden"
              >
                {/* Logout Button */}
                <button
                  onClick={async () => {
                    await authService.signOut()
                    navigate('/')
                  }}
                  className="w-full flex items-center space-x-3 p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors text-left"
                >
                  <LogOut className="w-5 h-5 text-orange-400" />
                  <div>
                    <span className="text-white font-medium">{t('settings.logout')}</span>
                    <p className="text-white/60 text-sm">{t('settings.logoutDesc')}</p>
                  </div>
                </button>


                {/* Danger Zone Toggle */}
                <button
                  onClick={() => setShowDangerZone(!showDangerZone)}
                  className="w-full flex items-center justify-between p-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-xl transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <div className="text-left">
                      <span className="text-red-300 font-medium">{t('settings.dangerZone')}</span>
                      <p className="text-red-400/80 text-sm">{t('settings.dangerZoneDesc')}</p>
                    </div>
                  </div>
                  {showDangerZone ? (
                    <ChevronUp className="w-5 h-5 text-red-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-red-400" />
                  )}
                </button>

                {/* Danger Zone Content */}
                {showDangerZone && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="ml-8 mt-2 overflow-hidden"
                  >
                    <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <Trash2 className="w-4 h-4 text-red-400" />
                        <span className="text-red-300 font-medium text-sm">{t('settings.delete.title')}</span>
                      </div>
                      <p className="text-red-400/80 text-xs mb-3">
                        {t('settings.deleteAccountDesc')}
                      </p>
                      <button
                        onClick={handleDeleteAccount}
                        disabled={deleting}
                        className="w-full py-2 px-3 bg-red-600/20 hover:bg-red-600/30 border border-red-500/50 rounded-lg text-red-300 font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {deleting ? (
                          <div className="flex items-center justify-center space-x-2">
                            <div className="w-3 h-3 border border-red-300/60 border-t-red-200 rounded-full animate-spin" />
                            <span>{t('settings.delete.deleting')}</span>
                          </div>
                        ) : (
                          t('settings.delete.title')
                        )}
                      </button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Help Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="space-y-4 mx-4"
      >
        <h2 className="text-xl font-bold text-white flex items-center space-x-2">
          <HelpCircle className="w-5 h-5 text-yellow-400" />
          <span>{t('settings.help')}</span>
        </h2>

        <div className="card-gradient">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="text-center space-y-2">
              <h3 className="font-semibold text-white">{t('settings.howToPlay')}</h3>
              <p className="text-white/60 text-sm">{t('settings.howToPlayDesc')}</p>
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold text-white">{t('settings.support')}</h3>
              <p className="text-white/60 text-sm">{t('settings.supportDesc')}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Action Buttons - Simplified */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="flex flex-col sm:flex-row gap-4 mx-4 pb-8"
      >
        <button
          onClick={handleSaveSettings}
          disabled={saving || !isDirty}
          className={`btn-primary flex items-center justify-center space-x-2 flex-1 ${
            !isDirty ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          <span>{saving ? t('settings.saving') : t('settings.save')}</span>
        </button>

        <button
          onClick={handleResetSettings}
          disabled={saving}
          className="btn-secondary flex items-center justify-center space-x-2 sm:w-auto"
        >
          <RotateCcw className="w-4 h-4" />
          <span>{t('settings.reset')}</span>
        </button>
      </motion.div>

      {/* Custom CSS for slider */}
      <style>
        {`
          .slider::-webkit-slider-thumb {
            appearance: none;
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: #8b5cf6;
            cursor: pointer;
            box-shadow: 0 0 10px rgba(139, 92, 246, 0.5);
          }
          
          .slider::-moz-range-thumb {
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: #8b5cf6;
            cursor: pointer;
            border: none;
            box-shadow: 0 0 10px rgba(139, 92, 246, 0.5);
          }
          
          .slider:disabled::-webkit-slider-thumb {
            background: #6b7280;
            box-shadow: none;
          }
          
          .slider:disabled::-moz-range-thumb {
            background: #6b7280;
            box-shadow: none;
          }
        `}
      </style>
    </div>
  )
}
